import { FC, useEffect, useRef, useState } from "react";

import useSound from "use-sound";
import { t } from "i18next";

import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { styles } from "./Timer.styles";

import timerDoneSound from "assets/sounds/timerDone.mp3";

/** Formats remaining milliseconds as mm:ss (countdown duration, not Unix time). */
const formatCountdownMmSs = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

interface TimerProps {
  timerLength: number;
  timerLocalStorageKey: string;
  setTimerDone: any;
  text: string;
}

const Timer: FC<TimerProps> = ({
  timerLength = 120,
  timerLocalStorageKey,
  setTimerDone,
  text,
}) => {
  const [timerValue, setTimerValue] = useState(() => timerLength * 1000);
  const [timerDone] = useSound(timerDoneSound);
  const countStartedAtRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();

  useEffect(() => {
    // Resume countdown after WebView reload instead of restarting from full duration
    const existingStartedAt = localStorage.getItem(timerLocalStorageKey)
    let startedAt: number | undefined

    if (existingStartedAt) {
      const parsed = Number(existingStartedAt)

      // Corrupt or non-numeric timestamps restart the timer instead of producing NaN
      if (!Number.isFinite(parsed) || parsed <= 0) {
        localStorage.removeItem(timerLocalStorageKey)
      } else {
        const elapsed = Date.now() - parsed

        if (elapsed >= timerLength * 1000) {
          // Timer finished while app was in background — enable Next immediately
          setTimerDone(true)
          setTimerValue(0)
          localStorage.removeItem(timerLocalStorageKey)
          return
        }

        startedAt = parsed
        setTimerValue(Math.max(0, timerLength * 1000 - elapsed))
      }
    }

    if (!startedAt) {
      startedAt = Date.now()
      localStorage.setItem(timerLocalStorageKey, String(startedAt))
    }

    countStartedAtRef.current = startedAt

    const runTick = (): void => {
      const start = countStartedAtRef.current
      if (start === null) {
        return
      }
      const timePassed = Math.ceil((Date.now() - start) / 1000)
      const remainingMs = Math.max(0, (timerLength - timePassed) * 1000)
      setTimerValue(remainingMs)

      if (timePassed > timerLength) {
        setTimerDone(true)
        setTimerValue(0)
        return
      }

      timeoutRef.current = setTimeout(runTick, 1000)
    }

    runTick()

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      countStartedAtRef.current = null
      setTimerValue(0)
      // Keep timerLocalStorageKey on unmount so WebView reload can resume elapsed time
    }
  }, [timerLength, timerLocalStorageKey, setTimerDone])

  useEffect(() => {
    timerValue === 0 && timerDone();
  }, [timerValue, timerDone]);

  return (
    <div className={classes.timerContainer}>
      <div className={classes.timerText2}>{t(text)}</div>
      <div className={classes.timer} data-test-id="timer-countdown">
        {formatCountdownMmSs(timerValue)}
      </div>
    </div>
  );
};

export default Timer;
